import React, { useState, useRef, useCallback, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import styled from "@emotion/styled";
import {
    PrivacyPolicyContent,
    TermsOfServiceContent,
} from "constant/legalDocuments";

const StyledModal = styled(Modal)`
    & .modal-dialog {
        max-width: 80%;
    }

    & .modal-content {
        max-height: 85vh;
        display: flex;
        flex-direction: column;
    }

    & .modal-header {
        background-color: #1c3e52;
        border-bottom: none;
        flex-shrink: 0;
        padding: 1rem 1.5rem;

        .modal-title {
            color: #ffffff;
            font-size: 1.125rem;
            font-weight: 500;
        }

        .close-button {
            color: #ffffff;
            opacity: 1;
            padding: 0;
            margin: 0;

            &:hover {
                color: #ffffff;
                opacity: 0.8;
            }

            .material-icons {
                font-size: 1.5rem;
            }
        }
    }

    & .modal-body {
        overflow-y: auto;
        flex: 1;
        padding: 1.5rem;
    }

    & .modal-footer {
        border-top: 1px solid #dee2e6;
        flex-shrink: 0;
        flex-direction: column;
        align-items: stretch;
    }
`;

const DocumentContent = styled.div`
    font-size: 14px;
    line-height: 1.6;
    color: #333333;

    h1 {
        font-size: 1.5rem;
        font-weight: 600;
        margin-bottom: 1rem;
        color: #1a1a1a;
    }

    h2 {
        font-size: 1.25rem;
        font-weight: 600;
        margin-top: 1.5rem;
        margin-bottom: 0.75rem;
        color: #2a2a2a;
    }

    h3 {
        font-size: 1.1rem;
        font-weight: 600;
        margin-top: 1rem;
        margin-bottom: 0.5rem;
        color: #333333;
    }

    p {
        margin-bottom: 0.75rem;
        color: #333333;
    }

    ul,
    ol {
        margin-bottom: 0.75rem;
        padding-left: 1.5rem;
        list-style-type: disc;
    }

    ol {
        list-style-type: decimal;
    }

    li {
        margin-bottom: 0.25rem;
        color: #333333;
    }

    a {
        color: #00bfa5;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }

    hr {
        margin: 2rem 0;
        border-top: 2px solid #dee2e6;
    }
`;

const CheckboxWrapper = styled.div<{ disabled: boolean }>`
    padding: 0.75rem 0;
    opacity: ${(props) => (props.disabled ? 0.6 : 1)};

    label {
        cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
        margin-bottom: 0;
        color: #333333;
        font-size: 14px;
    }

    a {
        color: #00bfa5;
        text-decoration: none;
        font-weight: 500;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const AcceptButton = styled(Button)`
    background-color: #00bfa5;
    border-color: #00bfa5;
    color: #ffffff;
    font-weight: 500;
    padding: 0.5rem 1.5rem;

    &:hover,
    &:focus {
        background-color: #00a896;
        border-color: #00a896;
    }

    &:disabled {
        background-color: #00bfa5;
        border-color: #00bfa5;
        opacity: 0.65;
    }
`;

export interface AcceptTermsModalProps {
    show: boolean;
    onHide: () => void;
    onAccept: () => void;
}

const AcceptTermsModal: React.FC<AcceptTermsModalProps> = ({
    show,
    onHide,
    onAccept,
}) => {
    const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
    const [isAccepted, setIsAccepted] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    // Reset state when modal opens
    useEffect(() => {
        if (show) {
            setHasScrolledToEnd(false);
            setIsAccepted(false);
        }
    }, [show]);

    // Check if content is scrollable and if scrolled to end
    const handleScroll = useCallback(() => {
        const element = contentRef.current;
        if (!element) return;

        const { scrollTop, scrollHeight, clientHeight } = element;
        const threshold = 20; // pixels from bottom to consider "at end"

        const isAtEnd = scrollTop + clientHeight >= scrollHeight - threshold;

        if (isAtEnd) {
            setHasScrolledToEnd(true);
        }
    }, []);

    // Check initial scroll state when content loads
    useEffect(() => {
        if (show && contentRef.current) {
            const element = contentRef.current;
            // If content doesn't need scrolling, enable checkbox immediately
            if (element.scrollHeight <= element.clientHeight) {
                setHasScrolledToEnd(true);
            }
        }
    }, [show]);

    const handleAcceptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (hasScrolledToEnd) {
            setIsAccepted(e.target.checked);
        }
    };

    const handleContinue = () => {
        if (isAccepted) {
            onAccept();
        }
    };

    return (
        <StyledModal
            show={show}
            onHide={onHide}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
        >
            <Modal.Header>
                <Modal.Title>Privacy Policy & Terms of Service</Modal.Title>
                <Button
                    variant=""
                    className="close-button"
                    onClick={onHide}
                    aria-label="Close"
                >
                    <span className="material-icons">close</span>
                </Button>
            </Modal.Header>
            <Modal.Body ref={contentRef} onScroll={handleScroll}>
                <DocumentContent>
                    <PrivacyPolicyContent />
                    <hr />
                    <TermsOfServiceContent />
                </DocumentContent>
            </Modal.Body>
            <Modal.Footer>
                <CheckboxWrapper disabled={!hasScrolledToEnd}>
                    <Form.Check
                        type="checkbox"
                        id="accept-terms-checkbox"
                        checked={isAccepted}
                        disabled={!hasScrolledToEnd}
                        onChange={handleAcceptChange}
                        label={
                            <span>
                                By creating an account, you are agreeing with our{" "}
                                <a
                                    href="#terms-of-service"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const element = document.querySelector("h1:last-of-type");
                                        element?.scrollIntoView({ behavior: "smooth" });
                                    }}
                                >
                                    Terms of Service
                                </a>{" "}
                                and{" "}
                                <a
                                    href="#privacy-policy"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const element = document.querySelector("h1:first-of-type");
                                        element?.scrollIntoView({ behavior: "smooth" });
                                    }}
                                >
                                    Privacy Policy
                                </a>{" "}
                                and you confirm that you are above 13 years of age.
                            </span>
                        }
                    />
                </CheckboxWrapper>
                <div className="d-flex justify-content-end mt-3">
                    <AcceptButton
                        onClick={handleContinue}
                        disabled={!isAccepted}
                    >
                        I Accept &amp; Continue
                    </AcceptButton>
                </div>
            </Modal.Footer>
        </StyledModal>
    );
};

export default AcceptTermsModal;
